import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, FormText, Label, Spinner } from "reactstrap";

import ProfessionalAvatar from "../../../../Components/Common/ProfessionalAvatar";
import ProfessionalPhotoCropModal, {
  PHOTO_CROP_MEMORY_NEW_KEY,
} from "./ProfessionalPhotoCropModal";

const ACCEPTED_ATTR = "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp";
const ALLOWED_MIME = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES = 5 * 1024 * 1024;

export type ProfessionalPhotoFieldProps = {
  name: string;
  colorHex: string;
  photoUrl?: string | null;
  busy?: boolean;
  disabled?: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
  canClear: boolean;
  /** Show a mini /book card so the salon sees how the photo looks online. */
  showBookPreview?: boolean;
  /** Crop framing memory key — professional id or omit for create (`new`). */
  cropMemoryKey?: string | number | null;
};

/** Optional portrait — drag/pick, adjustable square crop, optional /book preview. */
const ProfessionalPhotoField: React.FC<ProfessionalPhotoFieldProps> = ({
  name,
  colorHex,
  photoUrl,
  busy = false,
  disabled = false,
  onPick,
  onClear,
  canClear,
  showBookPreview = true,
  cropMemoryKey = null,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [cropSource, setCropSource] = useState<File | null>(null);
  const liveRegionRef = useRef<HTMLParagraphElement>(null);
  const lastAnnouncedUrl = useRef<string | null | undefined>(undefined);

  const memoryKey =
    cropMemoryKey != null && String(cropMemoryKey).trim() !== ""
      ? String(cropMemoryKey)
      : PHOTO_CROP_MEMORY_NEW_KEY;

  const acceptFile = useCallback(
    (file: File) => {
      const mimeOk = file.type === "" || ALLOWED_MIME.has(file.type);
      const extOk = /\.(jpe?g|png|webp)$/i.test(file.name);

      if (!mimeOk && !extOk) {
        toast.error(t("professionals.fields.photo.invalidType"));
        return;
      }

      if (file.size > MAX_BYTES) {
        toast.error(t("professionals.fields.photo.tooLarge"));
        return;
      }

      setCropSource(file);
    },
    [t]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (file) {
      acceptFile(file);
    }
  };

  useEffect(() => {
    if (!showBookPreview) {
      return;
    }
    if (lastAnnouncedUrl.current === undefined) {
      lastAnnouncedUrl.current = photoUrl ?? null;
      return;
    }
    if (lastAnnouncedUrl.current === (photoUrl ?? null)) {
      return;
    }
    lastAnnouncedUrl.current = photoUrl ?? null;
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = "";
      // Force screen readers to re-announce the same phrase when photo changes again.
      requestAnimationFrame(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = t(
            "professionals.fields.photo.bookPreviewLive"
          );
        }
      });
    }
  }, [photoUrl, showBookPreview, t]);

  const locked = busy || disabled;
  const displayName =
    name.trim() || t("professionals.fields.photo.placeholderName");

  return (
    <div className="mb-3">
      <Label className="d-block">{t("professionals.fields.photo.label")}</Label>
      <div className="d-flex flex-wrap gap-3 align-items-stretch">
        <div
          className={`d-flex flex-wrap align-items-center gap-3 rounded border p-3 flex-grow-1 ${
            dragging ? "border-primary bg-primary-subtle" : "border-dashed"
          }`}
          onDragEnter={(event) => {
            event.preventDefault();
            if (!locked) {
              setDragging(true);
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (locked) {
              return;
            }
            const file = event.dataTransfer.files?.[0];
            if (file) {
              acceptFile(file);
            }
          }}
        >
          <ProfessionalAvatar
            name={displayName}
            colorHex={colorHex}
            photoUrl={photoUrl}
            size={72}
          />
          <div className="d-flex flex-column gap-2 flex-grow-1">
            <div className="d-flex flex-wrap gap-2">
              <Button
                color="soft-primary"
                size="sm"
                type="button"
                disabled={locked}
                onClick={() => inputRef.current?.click()}
              >
                {busy ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    {t("professionals.fields.photo.uploading")}
                  </>
                ) : (
                  <>
                    <i className="ri-camera-line align-bottom me-1" />
                    {t("professionals.fields.photo.choose")}
                  </>
                )}
              </Button>
              {canClear ? (
                <Button
                  color="soft-danger"
                  size="sm"
                  type="button"
                  disabled={locked}
                  onClick={onClear}
                >
                  {t("professionals.fields.photo.remove")}
                </Button>
              ) : null}
            </div>
            <FormText className="mb-0">
              {dragging
                ? t("professionals.fields.photo.dropHere")
                : t("professionals.fields.photo.hint")}
            </FormText>
          </div>
        </div>

        {showBookPreview ? (
          <div
            className="rounded border bg-white p-3"
            style={{ minWidth: 200, maxWidth: 260 }}
            aria-label={t("professionals.fields.photo.bookPreviewLabel")}
          >
            <p className="text-muted small mb-2">
              {t("professionals.fields.photo.bookPreviewCaption")}
            </p>
            <div className="d-flex align-items-center gap-2 border rounded p-2">
              <ProfessionalAvatar
                name={displayName}
                colorHex={colorHex}
                photoUrl={photoUrl}
                size={48}
              />
              <div className="flex-grow-1 min-w-0">
                <div className="fw-semibold text-truncate fs-13">
                  {displayName}
                </div>
                <div className="text-muted fs-11">
                  {t("professionals.fields.photo.bookPreviewSelect")}
                </div>
              </div>
            </div>
            <p
              ref={liveRegionRef}
              className="visually-hidden"
              aria-live="polite"
              aria-atomic="true"
            />
          </div>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_ATTR}
        className="d-none"
        disabled={locked}
        onChange={handleChange}
      />
      <ProfessionalPhotoCropModal
        isOpen={Boolean(cropSource)}
        sourceFile={cropSource}
        memoryKey={memoryKey}
        onCancel={() => setCropSource(null)}
        onConfirm={(file) => {
          setCropSource(null);
          onPick(file);
        }}
      />
    </div>
  );
};

export default ProfessionalPhotoField;
